import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { TranslateService } from '@ngx-translate/core';
import { FirestoreService } from 'src/app/data/firestore.service';
import { HudlDataService, HudlScheduleEntry, HudlShareEntry } from 'src/app/data/hudl.service';
import { AuthService } from 'src/app/shared/auth.service';
import { RecordingSetup, Team, TeamRecordingSetup, VideoTool } from 'src/app/data/types';
import { EventNameService } from 'src/app/data/event-name.service';
import { ParseResponse, ParsedEvent, eventNameSuffix, PRACTICE_PLAN_MAX_FILE_SIZE_MB, PRACTICE_PLAN_MAX_ROWS, PRACTICE_PLAN_ALLOWED_MIME } from 'src/app/data/practice-plan.constants';
import { PrivacyOptions } from 'src/app/data/recording.service';
import { dateLessThan, timeRequired } from 'src/app/containers/form-validations/custom.validators';

@Component({
  selector: 'app-practice-plan-modal',
  templateUrl: './practice-plan-modal.component.html',
  styleUrls: ['./practice-plan-modal.component.scss'],
  standalone: false
})
export class PracticePlanModalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<any>();

  @Input() teams: Team[] = [];
  @Input() prefillTeamPath: string | null = null;
  @Output() formSubmitted = new EventEmitter<any[]>();

  @ViewChild('template', { static: true }) template!: TemplateRef<any>;
  modalRef!: BsModalRef;
  config = {
    backdrop: true,
    ignoreBackdropClick: true,
    class: 'modal-fullscreen',
  };

  form!: UntypedFormGroup;
  submitted = false;

  recordingSetups: Array<{ v: any; d: string }> = [];
  recordingSetupsLoading = false;
  videoTool = '';
  teamName = '';
  team: Team | null = null;
  user: any = null;
  hudlEvents: HudlScheduleEntry[] = [];
  hudlEventsLoading = false;
  hudlAvailableShareList: HudlShareEntry[] = [];
  hudlAvailableShareListLoading = false;
  private hudlDefaultsApplied = false;
  private hudlEventsSub: Subscription | null = null;
  private hudlShareSub: Subscription | null = null;
  private hudlLoadTeamId: string | null = null;

  selectedFile: File | null = null;
  fileError: string | null = null;
  uploading = false;
  parsing = false;
  submitting = false;
  generalError: string | null = null;

  lastParseResponse: ParseResponse | null = null;
  availableCategories: string[] = [];
  private lastUploadedSignature: string | null = null;
  private previousExcluded: string[] = [];
  private loadedTeamId: string | null = null;
  private practiceAnchorDate = new WeakMap<UntypedFormGroup, {date: Date; hasPrefix: boolean}>();
  private practiceParsedIndex = new WeakMap<UntypedFormGroup, number>();

  successCount = 0;
  failureCount = 0;

  readonly maxFileSizeMb = PRACTICE_PLAN_MAX_FILE_SIZE_MB;
  readonly maxRows = PRACTICE_PLAN_MAX_ROWS;
  readonly allowedMime = PRACTICE_PLAN_ALLOWED_MIME;
  readonly privacyOptions = PrivacyOptions;

  constructor(
    private db: FirestoreService,
    private modalService: BsModalService,
    private eventNameSvc: EventNameService,
    private functions: Functions,
    private translate: TranslateService,
    private auth: AuthService,
    private hudlDataService: HudlDataService,
  ) {}

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      team: new UntypedFormControl(null, [Validators.required]),
      videoPrivacy: new UntypedFormControl('unlisted'),
      bufferBefore: new UntypedFormControl(1),
      bufferAfter: new UntypedFormControl(1),
      excludedCategories: new UntypedFormControl([]),
      hudlEvent: new UntypedFormControl(null),
      hudlShare: new UntypedFormControl(null),
      practices: new UntypedFormArray([]),
    });

    this.db.get<any>('users', this.auth.user.uid).pipe(take(1)).subscribe({
      next: (data) => { this.user = data; },
      error: (err) => console.error(err),
    });

    this.form.get('team')!.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value) => this.onTeamChange(value));
    this.form.get('bufferBefore')!.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.applyDefaultBuffersToRows();
      this.persistCurrentBuffers();
    });
    this.form.get('bufferAfter')!.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.applyDefaultBuffersToRows();
      this.persistCurrentBuffers();
    });
    this.form.get('excludedCategories')!.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => this.applyCategoryChanges());
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  show(): void {
    const prefillTeam = this.prefillTeamPath && this.teams.some((team) => team.path === this.prefillTeamPath) ? this.prefillTeamPath : null;
    this.form.reset({
      team: prefillTeam,
      videoPrivacy: 'unlisted',
      bufferBefore: 1,
      bufferAfter: 1,
      excludedCategories: [],
      hudlEvent: null,
      hudlShare: null,
    }, { emitEvent: false });
    this.practicesArray.clear();
    this.appendPractice(null);
    this.recordingSetups = [];
    this.videoTool = '';
    this.teamName = '';
    this.team = null;
    this.hudlEvents = [];
    this.hudlAvailableShareList = [];
    this.hudlDefaultsApplied = false;
    this.hudlLoadTeamId = null;
    this.hudlEventsSub?.unsubscribe();
    this.hudlEventsSub = null;
    this.hudlShareSub?.unsubscribe();
    this.hudlShareSub = null;
    this.selectedFile = null;
    this.fileError = null;
    this.lastParseResponse = null;
    this.availableCategories = [];
    this.lastUploadedSignature = null;
    this.previousExcluded = [];
    this.loadedTeamId = null;
    this.submitted = false;
    this.successCount = 0;
    this.failureCount = 0;
    this.generalError = null;

    this.modalRef = this.modalService.show(this.template, this.config);

    if (prefillTeam) {
      this.onTeamChange(prefillTeam);
    }
  }

  get practicesArray(): UntypedFormArray {
    return this.form.get('practices') as UntypedFormArray;
  }

  practiceAt(index: number): UntypedFormGroup {
    return this.practicesArray.at(index) as UntypedFormGroup;
  }

  practiceRowsAt(index: number): UntypedFormArray {
    return this.practiceAt(index).get('rows') as UntypedFormArray;
  }

  totalRowsCount(): number {
    return this.practicesArray.controls.reduce((sum, p) => sum + ((p.get('rows') as UntypedFormArray).length), 0);
  }

  private appendPractice(date: Date | null, sourceLabel: string | null = null): UntypedFormGroup {
    const practiceGroup = new UntypedFormGroup({
      practiceDate: new UntypedFormControl(date, [Validators.required]),
      sourceLabel: new UntypedFormControl(sourceLabel),
      rows: new UntypedFormArray([]),
    });
    practiceGroup.get('practiceDate')!.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((newDate: Date | null) => {
      this.onPracticeDateChange(practiceGroup, newDate);
    });
    this.practicesArray.push(practiceGroup);
    return practiceGroup;
  }

  addPractice() {
    this.appendPractice(null);
  }

  removePractice(index: number) {
    if (this.practicesArray.length <= 1) {
      return;
    }
    this.practicesArray.removeAt(index);
  }

  private onTeamChange(value: string | null) {
    this.recordingSetups = [];
    this.recordingSetupsLoading = false;
    this.videoTool = '';
    this.teamName = '';
    this.team = null;
    this.hudlEvents = [];
    this.hudlAvailableShareList = [];
    this.hudlDefaultsApplied = false;
    this.hudlLoadTeamId = null;
    this.hudlEventsSub?.unsubscribe();
    this.hudlEventsSub = null;
    this.hudlShareSub?.unsubscribe();
    this.hudlShareSub = null;
    this.form.patchValue({ hudlEvent: null, hudlShare: null }, { emitEvent: false });
    if (!value) {
      return;
    }
    const team = this.teams.find((t) => t.path === value);
    if (!team) {
      return;
    }
    this.team = team;
    this.teamName = team.name;
    this.restoreBuffersForTeam(team.id);
    const capturedTeamPath = team.path;
    const isStillCurrent = () => this.form.get('team')!.value === capturedTeamPath;
    this.recordingSetupsLoading = true;
    this.db.get<TeamRecordingSetup>('teamRecordingSetups', team.id).pipe(take(1)).subscribe({
      next: (data) => {
        if (!isStillCurrent()) {
          return;
        }
        const setupRefs = data?.recordingSetups ?? [];
        if (setupRefs.length === 0) {
          this.recordingSetups = [];
          this.recordingSetupsLoading = false;
          return;
        }
        forkJoin(
          setupRefs.map((ref) => this.db.getByPath<RecordingSetup>(ref.v.path).pipe(take(1)))
        ).subscribe({
          next: (setups) => {
            if (!isStillCurrent()) {
              return;
            }
            this.recordingSetups = setupRefs.filter((_, i) => setups[i]?.active === true && setups[i]?.detectionMode?.v?.id !== 'equirectangular');
            this.recordingSetupsLoading = false;
            setTimeout(() => this.applyRecalledSetupsToRows(), 0);
          },
          error: (err) => {
            if (!isStillCurrent()) {
              return;
            }
            console.error(err);
            this.recordingSetups = setupRefs;
            this.recordingSetupsLoading = false;
            setTimeout(() => this.applyRecalledSetupsToRows(), 0);
          },
        });
      },
      error: (err) => {
        if (!isStillCurrent()) {
          return;
        }
        console.error(err);
        this.recordingSetupsLoading = false;
      },
    });
    if (team.videoTool) {
      this.db.get<VideoTool>('videoTools', team.videoTool.v.id).pipe(take(1)).subscribe({
        next: (data) => {
          if (!isStillCurrent()) {
            return;
          }
          if (data && data.tool) {
            this.videoTool = data.tool;
            if (this.videoTool === 'hudl') {
              this.hudlLoadTeamId = team.id;
              this.loadHudlEvents(team);
              this.loadHudlShareList(team);
            }
          }
        },
        error: (err) => {
          if (!isStillCurrent()) {
            return;
          }
          console.error(err);
        },
      });
    }
  }

  private loadHudlEvents(team: Team) {
    this.hudlEventsSub?.unsubscribe();
    this.hudlEventsLoading = true;
    const captured = team.id;
    this.hudlEventsSub = this.hudlDataService.loadEvents(team.path).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (events) => {
        if (this.hudlLoadTeamId !== captured) {
          return;
        }
        this.hudlEvents = events;
        this.hudlEventsLoading = false;
        if (!this.form.get('hudlEvent')!.value) {
          const closest = this.hudlDataService.pickClosestEvent(events, 7, 14);
          this.form.get('hudlEvent')!.patchValue(closest ? closest.id : null, { emitEvent: false });
        }
      },
      error: (err) => {
        console.error(err);
        this.hudlEventsLoading = false;
      },
    });
  }

  private loadHudlShareList(team: Team) {
    this.hudlShareSub?.unsubscribe();
    this.hudlAvailableShareListLoading = true;
    this.hudlDefaultsApplied = false;
    const captured = team.id;
    this.hudlShareSub = this.hudlDataService.loadShareList(team.path).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: ({ list }) => {
        if (this.hudlLoadTeamId !== captured) {
          return;
        }
        this.hudlAvailableShareList = list;
        if (!this.hudlDefaultsApplied) {
          const defaults = this.hudlDataService.computeDefaultShare(this.team, this.user, list);
          this.form.get('hudlShare')!.patchValue(defaults, { emitEvent: false });
          this.hudlDefaultsApplied = true;
        }
        this.hudlAvailableShareListLoading = false;
      },
      error: (err) => console.error(err),
    });
  }

  async refreshHudlEvents() {
    if (!this.team) {
      return;
    }
    this.hudlEventsLoading = true;
    await this.hudlDataService.refreshData(this.team.id, ['schedule']);
    this.hudlEventsLoading = false;
  }

  async refreshHudlMembersAndGroups() {
    if (!this.team) {
      return;
    }
    this.hudlAvailableShareListLoading = true;
    await this.hudlDataService.refreshData(this.team.id, ['membersAndGroups']);
    this.hudlAvailableShareListLoading = false;
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.fileError = null;
    if (!file) {
      this.selectedFile = null;
      return;
    }
    if (!this.allowedMime.includes(file.type)) {
      this.fileError = this.translate.instant('practicePlan.errors.unsupportedFile');
      this.selectedFile = null;
      return;
    }
    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.fileError = this.translate.instant('practicePlan.errors.fileTooLarge', { mb: this.maxFileSizeMb });
      this.selectedFile = null;
      return;
    }
    this.selectedFile = file;
  }

  async onLoadPlan() {
    this.generalError = null;
    if (!this.form.get('team')!.value) {
      this.generalError = this.translate.instant('practicePlan.errors.selectTeamFirst');
      return;
    }
    if (!this.selectedFile) {
      this.fileError = this.translate.instant('practicePlan.errors.chooseFile');
      return;
    }
    if (this.lastParseResponse && this.lastUploadedSignature && this.fileSignature(this.selectedFile) === this.lastUploadedSignature) {
      this.rebuildPracticesFromCache();
      return;
    }
    this.uploading = true;
    try {
      const teamPath = this.form.get('team')!.value as string;
      const teamId = teamPath.split('/').pop()!;
      const contentType = this.selectedFile.type;

      const getUploadUrl = httpsCallable<{ teamId: string; fileName: string; contentType: string }, { uploadUrl: string; gcsUri: string }>(
        this.functions,
        'getPracticePlanUploadUrl',
      );
      const urlResult = await getUploadUrl({ teamId, fileName: this.selectedFile.name, contentType });
      const { uploadUrl, gcsUri } = urlResult.data;

      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        body: this.selectedFile,
        headers: {
          'Content-Type': contentType,
          'x-goog-content-length-range': `0,${PRACTICE_PLAN_MAX_FILE_SIZE_MB * 1024 * 1024}`,
        },
      });
      if (!putResp.ok) {
        throw new Error(this.translate.instant('practicePlan.errors.uploadFailed', { status: putResp.status }));
      }
      this.uploading = false;
      this.parsing = true;

      const callable = httpsCallable<{ gcsUri: string; language?: string }, ParseResponse>(
        this.functions,
        'parsePracticePlan',
      );
      const result = await callable({ gcsUri, language: 'en' });
      this.lastParseResponse = result.data;

      this.canonicalizeCategories(this.lastParseResponse.practices || []);
      const allEvents = (this.lastParseResponse.practices || []).flatMap((p) => p.events || []);
      this.availableCategories = Array.from(new Set(allEvents.map((e) => e.category).filter((c) => !!c)));
      this.lastUploadedSignature = this.fileSignature(this.selectedFile);
      this.loadedTeamId = teamId;
      const canonicalByKey = new Map(this.availableCategories.map((c) => [c.toLowerCase(), c]));
      const restored = this.loadExcludedFromStorage(teamId)
        .map((c) => canonicalByKey.get((c || '').trim().toLowerCase()))
        .filter((c): c is string => !!c);
      this.form.get('excludedCategories')!.patchValue(restored, { emitEvent: false });
      this.previousExcluded = restored.slice();
      this.rebuildPracticesFromCache();
    } catch (err: any) {
      console.error(err);
      this.generalError = err?.message || this.translate.instant('practicePlan.errors.failedToParse');
    } finally {
      this.uploading = false;
      this.parsing = false;
    }
  }

  private fileSignature(file: File): string {
    return `${file.name}|${file.size}|${file.lastModified}`;
  }

  private excludedStorageKey(teamId: string): string {
    return `practicePlanExcludedCategories:${teamId}`;
  }

  private loadExcludedFromStorage(teamId: string): string[] {
    try {
      const raw = localStorage.getItem(this.excludedStorageKey(teamId));
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }

  private buffersStorageKey(teamId: string): string {
    return `practicePlanBuffers:${teamId}`;
  }

  private restoreBuffersForTeam(teamId: string) {
    let before = 1;
    let after = 1;
    try {
      const raw = localStorage.getItem(this.buffersStorageKey(teamId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.before === 'number') {
          before = Math.max(0, parsed.before);
        }
        if (parsed && typeof parsed.after === 'number') {
          after = Math.max(0, parsed.after);
        }
      }
    } catch {
      // ignore
    }
    this.form.patchValue({ bufferBefore: before, bufferAfter: after }, { emitEvent: false });
  }

  private persistCurrentBuffers() {
    const teamPath = this.form.get('team')!.value as string | null;
    if (!teamPath) {
      return;
    }
    const teamId = teamPath.split('/').pop();
    if (!teamId) {
      return;
    }
    const before = Number(this.form.get('bufferBefore')!.value) || 0;
    const after = Number(this.form.get('bufferAfter')!.value) || 0;
    try {
      localStorage.setItem(this.buffersStorageKey(teamId), JSON.stringify({ before, after }));
    } catch {
      // ignore
    }
  }

  private saveExcludedToStorage(teamId: string, excluded: string[]) {
    try {
      if (excluded.length > 0) {
        localStorage.setItem(this.excludedStorageKey(teamId), JSON.stringify(excluded));
      } else {
        localStorage.removeItem(this.excludedStorageKey(teamId));
      }
    } catch {
      // localStorage unavailable; ignore
    }
  }

  private applyCategoryChanges() {
    if (!this.lastParseResponse) {
      this.previousExcluded = (this.form.get('excludedCategories')!.value || []).slice();
      return;
    }
    const newExcluded: string[] = (this.form.get('excludedCategories')!.value || []).slice();
    const newlyExcluded = newExcluded.filter((c) => !this.previousExcluded.includes(c));
    const newlyIncluded = this.previousExcluded.filter((c) => !newExcluded.includes(c));

    if (newlyExcluded.length > 0) {
      this.practicesArray.controls.forEach((practiceCtrl) => {
        const rowsArray = practiceCtrl.get('rows') as UntypedFormArray;
        for (let i = rowsArray.length - 1; i >= 0; i--) {
          const row = rowsArray.at(i) as UntypedFormGroup;
          if (newlyExcluded.includes(row.get('category')!.value)) {
            rowsArray.removeAt(i);
          }
        }
      });
    }

    if (newlyIncluded.length > 0) {
      const before = Number(this.form.get('bufferBefore')!.value) || 0;
      const after = Number(this.form.get('bufferAfter')!.value) || 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const parsedPractices = this.lastParseResponse.practices || [];
      const parsedToGroup = new Map<number, UntypedFormGroup>();
      this.practicesArray.controls.forEach((g) => {
        const idx = this.practiceParsedIndex.get(g as UntypedFormGroup);
        if (idx !== undefined) {
          parsedToGroup.set(idx, g as UntypedFormGroup);
        }
      });
      let droppedFromRemoved = 0;
      parsedPractices.forEach((parsedPractice, parsedIdx) => {
        const matching = (parsedPractice.events || []).filter((e) => newlyIncluded.includes(e.category));
        const practiceGroup = parsedToGroup.get(parsedIdx);
        if (!practiceGroup) {
          droppedFromRemoved += matching.length;
          return;
        }
        const {anchor, withPrefix} = this.getRowAnchorContext(practiceGroup, today);
        const rowsArray = practiceGroup.get('rows') as UntypedFormArray;
        matching.forEach((e) => rowsArray.push(this.makeRowFromEvent(e, anchor, before, after, withPrefix)));
        this.sortRowsByStart(rowsArray);
      });
      this.generalError = droppedFromRemoved > 0
        ? this.translate.instant('practicePlan.errors.reincludedDropped', { count: droppedFromRemoved })
        : null;
    }

    this.previousExcluded = newExcluded;

    if (this.loadedTeamId) {
      this.saveExcludedToStorage(this.loadedTeamId, newExcluded);
    }
  }

  private sortRowsByStart(rowsArray: UntypedFormArray) {
    const controls = (rowsArray.controls.slice() as UntypedFormGroup[]).sort((a, b) => {
      const sa = a.get('start')!.value as Date;
      const sb = b.get('start')!.value as Date;
      if (!sa || !sb) {
        return 0;
      }
      return sa.getTime() - sb.getTime();
    });
    while (rowsArray.length > 0) {
      rowsArray.removeAt(0);
    }
    controls.forEach((c) => rowsArray.push(c));
  }

  private rebuildPracticesFromCache() {
    if (!this.lastParseResponse) {
      return;
    }
    this.practicesArray.clear();

    const excluded: string[] = this.form.get('excludedCategories')!.value || [];
    const bufferBefore: number = Number(this.form.get('bufferBefore')!.value) || 0;
    const bufferAfter: number = Number(this.form.get('bufferAfter')!.value) || 0;

    const parsedPractices = this.lastParseResponse.practices || [];
    if (parsedPractices.length === 0) {
      this.appendPractice(null);
      this.previousExcluded = excluded.slice();
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let totalEventCount = 0;
    const droppedReport: string[] = [];
    parsedPractices.forEach((parsed, idx) => {
      const detectedDate = parsed.detectedDate ? this.parseIsoDate(parsed.detectedDate) : null;
      const practiceGroup = this.appendPractice(detectedDate, parsed.sourceLabel ?? null);
      this.practiceParsedIndex.set(practiceGroup, idx);
      const rowsArray = practiceGroup.get('rows') as UntypedFormArray;
      const events = (parsed.events || [])
        .filter((e) => !excluded.includes(e.category))
        .slice()
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
      const remainingBudget = Math.max(0, this.maxRows - totalEventCount);
      const slice = events.slice(0, remainingBudget);
      const dropped = events.length - slice.length;
      if (dropped > 0) {
        const label = parsed.sourceLabel || this.translate.instant('practicePlan.untitledPractice', { n: idx + 1 });
        droppedReport.push(`${label}: ${dropped}`);
      }
      totalEventCount += slice.length;
      const {anchor, withPrefix} = this.getRowAnchorContext(practiceGroup, today);
      slice.forEach((event) => rowsArray.push(this.makeRowFromEvent(event, anchor, bufferBefore, bufferAfter, withPrefix)));
    });

    if (droppedReport.length > 0) {
      this.generalError = this.translate.instant('practicePlan.errors.exceedsMax', { max: this.maxRows, dropped: droppedReport.join('; ') });
    } else {
      this.generalError = null;
    }

    this.previousExcluded = excluded.slice();
  }

  private makeRowFromEvent(event: ParsedEvent, practiceDate: Date, bufferBefore: number, bufferAfter: number, withPrefix: boolean): UntypedFormGroup {
    const start = this.combineDateTime(practiceDate, event.startTime, 0);
    const end = this.combineDateTime(practiceDate, event.endTime, 0);
    const suffix = eventNameSuffix(event) || this.translate.instant('practicePlan.untitledEvent');
    const eventName = withPrefix ? `${this.eventNameSvc.datePrefix(start)} - ${suffix}` : suffix;
    const recalledSetup = this.recallSetupForActivity(event.detail || event.category);
    return new UntypedFormGroup(
      {
        start: new UntypedFormControl(start, [timeRequired()]),
        end: new UntypedFormControl(end, [timeRequired()]),
        recordingSetup: new UntypedFormControl(recalledSetup, [Validators.required]),
        eventName: new UntypedFormControl(eventName, [Validators.required]),
        bufferBefore: new UntypedFormControl(bufferBefore),
        bufferAfter: new UntypedFormControl(bufferAfter),
        category: new UntypedFormControl(event.category),
        detail: new UntypedFormControl(event.detail || ''),
        errorMessage: new UntypedFormControl(null),
      },
      dateLessThan('start', 'end'),
    );
  }

  private setupByActivityStorageKey(teamId: string): string {
    return `practicePlanSetupByActivity:${teamId}`;
  }

  private getActiveTeamId(): string | null {
    if (this.loadedTeamId) {
      return this.loadedTeamId;
    }
    const teamPath = this.form.get('team')!.value as string | null;
    if (!teamPath) {
      return null;
    }
    return teamPath.split('/').pop() || null;
  }

  private normalizeActivityKey(activity: string | null | undefined): string {
    return (activity || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private loadSetupByActivityMap(teamId: string): Record<string, any> {
    try {
      const raw = localStorage.getItem(this.setupByActivityStorageKey(teamId));
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private recallSetupForActivity(activity: string | null | undefined): any {
    const teamId = this.getActiveTeamId();
    const key = this.normalizeActivityKey(activity);
    if (!teamId || !key) {
      return null;
    }
    const map = this.loadSetupByActivityMap(teamId);
    const stored = map[key];
    if (!stored || !stored.path) {
      console.log(`[recall] no entry for activity key "${key}" (raw "${activity}"); known keys:`, Object.keys(map));
      return null;
    }
    const matchingSetup = this.recordingSetups.find((s) => s.v?.path === stored.path);
    if (!matchingSetup) {
      console.log(`[recall] activity "${key}" found stored path "${stored.path}" but no matching recordingSetup loaded yet (have ${this.recordingSetups.length})`);
      return null;
    }
    console.log(`[recall] activity "${key}" → setup ${stored.path}`);
    return matchingSetup.v;
  }

  private applyRecalledSetupsToRows() {
    if (!this.getActiveTeamId() || this.recordingSetups.length === 0) {
      return;
    }
    this.practicesArray.controls.forEach((practiceCtrl) => {
      const rowsArray = practiceCtrl.get('rows') as UntypedFormArray;
      (rowsArray.controls as UntypedFormGroup[]).forEach((row) => {
        if (row.get('recordingSetup')!.value) {
          return;
        }
        const activity = (row.get('detail')!.value as string) || (row.get('category')!.value as string);
        const recalled = this.recallSetupForActivity(activity);
        if (recalled) {
          row.patchValue({ recordingSetup: recalled });
        }
      });
    });
  }

  private persistSetupByActivityFromRows() {
    const teamId = this.getActiveTeamId();
    if (!teamId) {
      return;
    }
    const map = this.loadSetupByActivityMap(teamId);
    let dirty = false;
    this.practicesArray.controls.forEach((practiceCtrl) => {
      const rowsArray = practiceCtrl.get('rows') as UntypedFormArray;
      (rowsArray.controls as UntypedFormGroup[]).forEach((row) => {
        const rawActivity = (row.get('detail')!.value as string) || (row.get('category')!.value as string);
        const key = this.normalizeActivityKey(rawActivity);
        const setup = row.get('recordingSetup')!.value;
        if (!key || !setup?.path) {
          return;
        }
        const existing = map[key];
        if (!existing || existing.path !== setup.path) {
          map[key] = { path: setup.path };
          dirty = true;
        }
      });
    });
    if (dirty) {
      try {
        localStorage.setItem(this.setupByActivityStorageKey(teamId), JSON.stringify(map));
        console.log(`practice plan saved setup-by-activity for team ${teamId}`, map);
      } catch (err) {
        console.warn('failed to save setup-by-activity', err);
      }
    } else {
      console.log(`practice plan setup-by-activity unchanged for team ${teamId}`);
    }
  }

  private canonicalizeCategories(practices: Array<{events?: ParsedEvent[]}>) {
    const keyToDisplay = new Map<string, string>();
    const isMixedCase = (s: string) => s !== s.toLowerCase() && s !== s.toUpperCase();
    for (const practice of practices) {
      for (const ev of practice.events || []) {
        const trimmed = (ev.category || '').trim();
        if (!trimmed) {
          continue;
        }
        const key = trimmed.toLowerCase();
        const existing = keyToDisplay.get(key);
        if (!existing) {
          keyToDisplay.set(key, trimmed);
        } else if (!isMixedCase(existing) && isMixedCase(trimmed)) {
          keyToDisplay.set(key, trimmed);
        }
      }
    }
    for (const practice of practices) {
      for (const ev of practice.events || []) {
        const trimmed = (ev.category || '').trim();
        if (!trimmed) {
          ev.category = '';
          continue;
        }
        ev.category = keyToDisplay.get(trimmed.toLowerCase())!;
      }
    }
  }

  private getRowAnchorContext(practiceGroup: UntypedFormGroup, today: Date): {anchor: Date; withPrefix: boolean} {
    const realDate = practiceGroup.get('practiceDate')!.value as Date | null;
    if (realDate) {
      this.practiceAnchorDate.set(practiceGroup, {date: realDate, hasPrefix: true});
      return {anchor: realDate, withPrefix: true};
    }
    const existing = this.practiceAnchorDate.get(practiceGroup);
    if (existing) {
      return {anchor: existing.date, withPrefix: existing.hasPrefix};
    }
    this.practiceAnchorDate.set(practiceGroup, {date: today, hasPrefix: false});
    return {anchor: today, withPrefix: false};
  }


  private onPracticeDateChange(practiceGroup: UntypedFormGroup, newDate: Date | null) {
    if (!newDate) {
      return;
    }
    const oldEntry = this.practiceAnchorDate.get(practiceGroup);
    this.practiceAnchorDate.set(practiceGroup, {date: newDate, hasPrefix: true});
    const rowsArray = practiceGroup.get('rows') as UntypedFormArray;
    if (rowsArray.length === 0) {
      return;
    }
    const oldPrefix = (oldEntry && oldEntry.hasPrefix) ? this.eventNameSvc.datePrefix(oldEntry.date) : null;
    const newPrefix = this.eventNameSvc.datePrefix(newDate);

    (rowsArray.controls as UntypedFormGroup[]).forEach((row) => {
      const start = row.get('start')!.value as Date | null;
      const end = row.get('end')!.value as Date | null;
      if (start) {
        const newStart = new Date(start);
        newStart.setFullYear(newDate.getFullYear());
        newStart.setMonth(newDate.getMonth());
        newStart.setDate(newDate.getDate());
        row.patchValue({ start: newStart }, { emitEvent: false });
      }
      if (end) {
        const newEnd = new Date(end);
        newEnd.setFullYear(newDate.getFullYear());
        newEnd.setMonth(newDate.getMonth());
        newEnd.setDate(newDate.getDate());
        row.patchValue({ end: newEnd }, { emitEvent: false });
      }

      const currentName: string = row.get('eventName')!.value || '';
      if (oldPrefix && currentName.startsWith(oldPrefix)) {
        row.patchValue({ eventName: newPrefix + currentName.slice(oldPrefix.length) }, { emitEvent: false });
      } else if (!oldPrefix && currentName) {
        row.patchValue({ eventName: `${newPrefix} - ${currentName}` }, { emitEvent: false });
      }
    });
  }

  private applyDefaultBuffersToRows() {
    const before: number = Number(this.form.get('bufferBefore')!.value) || 0;
    const after: number = Number(this.form.get('bufferAfter')!.value) || 0;
    this.practicesArray.controls.forEach((practiceCtrl) => {
      const rowsArray = practiceCtrl.get('rows') as UntypedFormArray;
      (rowsArray.controls as UntypedFormGroup[]).forEach((row) => {
        row.patchValue({ bufferBefore: before, bufferAfter: after });
      });
    });
  }

  toggleCategory(category: string) {
    const excluded: string[] = (this.form.get('excludedCategories')!.value || []).slice();
    const index = excluded.indexOf(category);
    if (index === -1) {
      excluded.push(category);
    } else {
      excluded.splice(index, 1);
    }
    this.form.get('excludedCategories')!.patchValue(excluded);
  }

  isExcluded(category: string): boolean {
    return ((this.form.get('excludedCategories')!.value || []) as string[]).includes(category);
  }

  removeRow(practiceIdx: number, rowIdx: number) {
    const rowsArray = this.practiceRowsAt(practiceIdx);
    rowsArray.removeAt(rowIdx);
  }

  addRow(practiceIdx: number) {
    const practiceGroup = this.practiceAt(practiceIdx);
    const practiceDate = practiceGroup.get('practiceDate')!.value as Date | null;
    if (!practiceDate) {
      this.generalError = this.translate.instant('practicePlan.errors.pickPracticeDate');
      return;
    }
    const before = Number(this.form.get('bufferBefore')!.value) || 0;
    const after = Number(this.form.get('bufferAfter')!.value) || 0;
    const row = new UntypedFormGroup(
      {
        start: new UntypedFormControl(null, [timeRequired()]),
        end: new UntypedFormControl(null, [timeRequired()]),
        recordingSetup: new UntypedFormControl(null, [Validators.required]),
        eventName: new UntypedFormControl('', [Validators.required]),
        bufferBefore: new UntypedFormControl(before),
        bufferAfter: new UntypedFormControl(after),
        category: new UntypedFormControl(''),
        detail: new UntypedFormControl(''),
        errorMessage: new UntypedFormControl(null),
      },
      dateLessThan('start', 'end'),
    );
    (practiceGroup.get('rows') as UntypedFormArray).push(row);
  }

  mergeWithNext(practiceIdx: number, rowIdx: number) {
    const rowsArray = this.practiceRowsAt(practiceIdx);
    if (rowIdx < 0 || rowIdx >= rowsArray.length - 1) {
      return;
    }
    const first = rowsArray.at(rowIdx) as UntypedFormGroup;
    const second = rowsArray.at(rowIdx + 1) as UntypedFormGroup;
    const firstEnd = first.get('end')!.value as Date;
    const secondEnd = second.get('end')!.value as Date;
    const mergedEnd = secondEnd && firstEnd && secondEnd.getTime() > firstEnd.getTime() ? secondEnd : firstEnd;
    first.patchValue({
      end: mergedEnd,
      bufferAfter: second.get('bufferAfter')!.value,
    });
    rowsArray.removeAt(rowIdx + 1);
  }

  get isFormValid(): boolean {
    if (!this.form.get('team')!.value) {
      return false;
    }
    if (this.practicesArray.length === 0) {
      return false;
    }
    if (this.totalRowsCount() === 0) {
      return false;
    }
    return this.practicesArray.valid;
  }

  async onSubmit() {
    this.submitted = true;
    if (!this.isFormValid) {
      return;
    }
    this.submitting = true;
    this.successCount = 0;
    this.failureCount = 0;

    const teamPath = this.form.get('team')!.value as string;
    const isHudl = this.videoTool === 'hudl';
    const videoPrivacy = this.form.get('videoPrivacy')!.value;
    const hudlEventId = isHudl ? this.form.get('hudlEvent')!.value : null;
    const hudlEvent = hudlEventId ? this.hudlEvents.find((e) => e.id === hudlEventId) : null;
    const hudlSeason = hudlEvent ? hudlEvent.seasonId : null;
    const hudlShare = isHudl ? (this.form.get('hudlShare')!.value || []) : null;

    const teamRef = this.db.getDocumentReference(teamPath);

    const records: any[] = [];
    this.practicesArray.controls.forEach((practiceCtrl) => {
      const practiceDate = practiceCtrl.get('practiceDate')!.value as Date | null;
      const rowsArray = practiceCtrl.get('rows') as UntypedFormArray;
      (rowsArray.controls as UntypedFormGroup[]).forEach((row) => {
        const rawStart: Date = row.get('start')!.value;
        const rawEnd: Date = row.get('end')!.value;
        const before = Number(row.get('bufferBefore')!.value) || 0;
        const after = Number(row.get('bufferAfter')!.value) || 0;
        const start = new Date(rawStart);
        const end = new Date(rawEnd);
        if (practiceDate) {
          start.setFullYear(practiceDate.getFullYear(), practiceDate.getMonth(), practiceDate.getDate());
          end.setFullYear(practiceDate.getFullYear(), practiceDate.getMonth(), practiceDate.getDate());
        }
        start.setMinutes(start.getMinutes() - before);
        end.setMinutes(end.getMinutes() + after);
        records.push({
          team: teamRef,
          recordingSetup: row.get('recordingSetup')!.value,
          eventName: row.get('eventName')!.value,
          videoPrivacy,
          liveBroadcast: false,
          // Practices never get a Replay event. Set explicitly: when it is left out the backend writes the default,
          // which re-planned the recording and gave a recording planned after the fact a live job.
          includeReplayEvent: false,
          hudlCategory: isHudl ? 5 : null,
          hudlEvent: isHudl ? hudlEventId : null,
          hudlSeason: isHudl ? hudlSeason : null,
          hudlShare: isHudl ? hudlShare : null,
          vidswapType: this.videoTool === 'vidswap' ? 'practice' : null,
          opponentTeam: null,
          homeTeam: null,
          rtmpBroadcastLink: null,
          youtubeBroadcastKey: null,
          start,
          end,
          state: 'submitted',
        });
      });
    });

    this.persistSetupByActivityFromRows();
    this.formSubmitted.emit(records);
    this.modalRef.hide();
    this.submitting = false;
  }

  private parseIsoDate(iso: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) {
      return null;
    }
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  private combineDateTime(date: Date, hhmm: string, bufferMinutes: number): Date {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
    const hours = m ? Number(m[1]) : 0;
    const minutes = m ? Number(m[2]) : 0;
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes, 0, 0);
    if (bufferMinutes !== 0) {
      result.setMinutes(result.getMinutes() + bufferMinutes);
    }
    return result;
  }

}
